/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardZeroData" />
import { IBacklogLevelConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import * as React from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager, usePageContext, useService } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { ZeroData } from "VSSUI/ZeroData";
import { ClientWorkItem } from "Wit/ClientWorkItemService/IClientWorkItem";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { getBacklogUrl } from "Wit/SprintsHub/SprintsView/Utils/RoutingUtils";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { ITaskboardService } from "../../Services/ITaskboardService";

interface ITaskboardZeroDataProps {
    backlogConfiguration: IBacklogLevelConfiguration | undefined;
    selectedIteration: IIterationData | undefined;
    teamSettings: ITeamSettings;
}

export const TaskboardZeroData: React.FunctionComponent<ITaskboardZeroDataProps> = ({ backlogConfiguration, selectedIteration, teamSettings }) => {
    const pageContext = usePageContext();
    const taskboardService = useService<ITaskboardService>("ITaskboardService");
    return (
        <ZeroData
            className="full-width"
            imageAltText={Resources.NoWorkScheduled}
            imagePath={getAssetLocation(pageContext, "ms.vss-work-web/common-backlog-content/Content/zerodata-no-work-scheduled.svg")}
            primaryText={Resources.NoWorkScheduled}
            secondaryText={
                <div>
                    <div className="margin-bottom-16">
                        <Link
                            href={getBacklogUrl(
                                pageContext,
                                teamSettings?.projectId || "",
                                teamSettings?.teamId || "",
                                backlogConfiguration?.defaultWorkItemType ?? "",
                                "planning"
                            )}
                        >
                            {Resources.ScheduleWork}
                        </Link>
                        {Resources.CreateOrScheduleWork}
                    </div>
                    <Button
                        primary={true}
                        disabled={!backlogConfiguration?.defaultWorkItemType}
                        onClick={() => {
                            showNewTaskboardWorkItemDialog(
                                pageContext,
                                taskboardService,
                                selectedIteration?.friendlyPath || "",
                                teamSettings,
                                backlogConfiguration
                            );
                        }}
                        iconProps={{ iconName: "Add" }}
                    >
                        {Resources.NewWorkItem}
                    </Button>
                </div>
            }
        />
    );
};

export function showNewTaskboardWorkItemDialog(
    pageContext: IVssPageContext,
    taskboardService: ITaskboardService,
    selectedIterationPath: string,
    teamSettings?: ITeamSettings,
    backlogConfiguration?: IBacklogLevelConfiguration,
    workItemType?: string
) {
    const initialValues: { [fieldName: string]: any } = {};
    if (selectedIterationPath) {
        initialValues[CoreFieldRefNames.IterationPath] = selectedIterationPath;
    }
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(onDismiss => (
        <WorkItemFormDialog
            onDismiss={onDismiss}
            workItemTypeName={workItemType || backlogConfiguration?.defaultWorkItemType!}
            initialValues={initialValues}
            onSaveComplete={async (workItem: IWorkItem) => {
                const fieldMap: INamedFieldDataDictionary = {};
                const fieldData = workItem.getFieldData();
                for (const [key, value] of Object.entries(fieldData)) {
                    const referenceName = workItem.fieldMapById[parseInt(key)].fieldDefinition.referenceName;
                    fieldMap[referenceName] = value;
                }

                const clientItem = new ClientWorkItem(fieldMap);

                if (teamSettings) {
                    clientItem.setFieldValue(teamSettings.teamFieldName, teamSettings.teamFieldDefaultValue);
                }
                clientItem.save(pageContext);
                taskboardService.reloadTaskBoardData();
            }}
        />
    ));
}
